import { useState, useCallback } from 'react'

export interface InputState {
  /**
   * User has changed the value
   */
  isDirty: boolean
  /**
   * User has interacted with the iniput
   */
  isTouched: boolean
  /**
   * The input has called onFocus
   */
  isFocused: boolean
  /**
   * The input has called onBlur
   */
  isBlurred: boolean
}

interface IGetErrorMessageOptions {
  /**
   * The kind of input states to consider/evaluate when conditionally
   * returning an error message
   * @default []
   */
  inputStates?: (keyof InputState)[]
  /**
   * Can pass in any kind of data, which will all be evaluated to be
   * truthy or falsey
   * @default []
   */
  conditions?: any[]
}

const getDefaultState = (): InputState => ({
  isDirty: false,
  isTouched: false,
  isBlurred: false,
  isFocused: false,
})

/**
 * Creates a simple input state that can return an error message
 * based on conditions passed to the hook
 */
export const useInputState = () => {
  const [inputState, setInputState] = useState<InputState>(getDefaultState())

  const onChangeTrigger = useCallback(() => {
    setInputState(prev => ({
      ...prev,
      isDirty: true,
      isTouched: true,
    }))
  }, [])

  const onFocusTrigger = useCallback(() => {
    setInputState(prev => ({
      ...prev,
      isTouched: true,
      isFocused: true,
      isBlurred: false,
    }))
  }, [])

  const onBlurTrigger = useCallback(() => {
    setInputState(prev => ({
      ...prev,
      isBlurred: true,
      isTouched: true,
      isFocused: false,
    }))
  }, [])

  const resetState = useCallback(() => {
    setInputState(getDefaultState())
  }, [])

  /**
   * Returns error message based on an array of conditions
   * being `true`
   */
  const getErrorMessage = useCallback(
    (message: string, { inputStates = [], conditions = [] }: IGetErrorMessageOptions) => {
      const booleanArray: boolean[] = []
      const conditionArray: any[] = []

      for (const state of inputStates) {
        booleanArray.push(!!inputState[state])
      }

      for (const condition of conditions) {
        conditionArray.push(!!condition)
      }

      return !booleanArray.includes(false) && !conditionArray.includes(false) ? message : undefined
    },
    [inputState]
  )

  const { isBlurred, isDirty, isFocused, isTouched } = inputState || {}

  return {
    isDirty,
    isBlurred,
    isFocused,
    isTouched,
    onChangeTrigger,
    onFocusTrigger,
    onBlurTrigger,
    getErrorMessage,
    resetState,
  }
}
